/**
 * @file WorkerTable.jsx
 * @description WorkerTable component for displaying and managing worker table.
 * @component WorkerTable
 * @exports WorkerTable - Main component for displaying and managing worker table.
 */

import Box from '@mui/material/Box';
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from '@mui/material/LinearProgress';
import TablePagination from "@mui/material/TablePagination";
import { SIZES, SORT_ORDERS, ALIGN, HTML_TAGS } from "../../../common/constants/commonConstants.js";
import { WORKER_TABLE_COLUMN_KEYS, WORKER_MESSAGES } from "../../../common/constants/ivrModule/workerConstants.js";

const WorkerTable = ({
    page,
    order,
    setPage,
    orderBy,
    workers,
    rowsPerPage,
    columns,
    setRowsPerPage,
    onEdit,
    onDelete,
    handleRequestSort,
    filteredWorkerLength,
    isWorkerLoading,
}) => {
    return (
        <>
            <TableContainer sx={{ mt: 2 }}>
                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={orderBy === column.id ? order : false}
                                    sx={{
                                        width:
                                            column.id === WORKER_TABLE_COLUMN_KEYS.ACTIONS ? 75 :
                                            column.id === WORKER_TABLE_COLUMN_KEYS.ID ? 150 :
                                            column.id === WORKER_TABLE_COLUMN_KEYS.STATE ? 100 :
                                            column.id === WORKER_TABLE_COLUMN_KEYS.QUEUE ? 200 :
                                            column.id === WORKER_TABLE_COLUMN_KEYS.SKILL ? 100 :
                                            column.id === WORKER_TABLE_COLUMN_KEYS.JOB ? 250 :
                                            column.id === WORKER_TABLE_COLUMN_KEYS.TIME ? 250 :
                                            200,
                                    }}
                                >
                                    {column.isSortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                            onClick={() =>
                                                handleRequestSort(column.id)
                                            }
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isWorkerLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER} >
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : workers.length > 0 ? workers.map((worker) => (
                            <TableRow hover tabIndex={-1} key={worker.id}>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <IconButton
                                            onClick={() => onEdit(worker)}
                                            disabled={worker.state !== "active"}
                                        >
                                            <PersonOffIcon fontSize={SIZES.SMALL} />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => onDelete(worker)}
                                        >
                                            <DeleteIcon fontSize={SIZES.SMALL} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {worker.id}
                                </TableCell>  
                                <TableCell>
                                    {worker.state}
                                </TableCell>
                                <TableCell>
                                    {worker.queue}
                                </TableCell>
                                <TableCell>
                                    {worker.skill}
                                </TableCell>
                                <TableCell>
                                    {worker.assignedJobId}
                                </TableCell>
                                <TableCell>
                                    {worker.assignedAtDate
                                    ? new Date(worker.assignedAtDate).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                        })
                                    : '-'}
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    {WORKER_MESSAGES.NO_WORKER}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={filteredWorkerLength}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPage(0);
                        setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                />
            </TableContainer>
        </>
    )
};

export default WorkerTable;